export const DESIGN_SETTINGS_KEY  = 'ebdaa_design_settings_v1';
export const BRAND_SETTINGS_KEY   = 'ebdaa_brand_settings_v1';
export const IDENTITY_SETTINGS_KEY = 'ebdaa_identity_settings_v1';

export interface SectionBgConfig {
  bgId: string;
  customColor: string;
}

export interface DesignSettings {
  colorPresetId: string;
  fontId: string;
  radiusId: string;
  buttonStyleId: string;
  layoutId: string;
  heroStyleId: string;
  cardStyleId: string;
  darkMode: boolean;
  animationsEnabled: boolean;
  productGridCols: number;
  pageBgId: string;
  pageBgCustomColor: string;
  // Per-section backgrounds
  sectionProducts: SectionBgConfig;
  sectionDevices: SectionBgConfig;
  sectionReviews: SectionBgConfig;
  sectionContact: SectionBgConfig;
}

export interface BrandSettings {
  navbarStyle: string;
  navbarHeight: string;
  navbarSticky: boolean;
  navbarShowSearch: boolean;
  navbarShowWishlist: boolean;
  navbarShowRewards: boolean;
  footerStyle: string;
  footerLayout: string;
  footerShowSocial: boolean;
  footerShowPayments: boolean;
  footerShowHours: boolean;
  footerCopyright: string;
  footerTagline: string;
}

export interface IdentitySettings {
  logoText: string;
  logoSubtext: string;
  logoTextStyle: string;
  logoShape: string;
  logoIconId: string;
  logoImageUrl: string;
  useLogoImage: boolean;
  heroImageId: string;
  heroImageUrl: string;
  useCustomHeroImage: boolean;
  heroOverlayOpacity: number;
  heroTitle: string;
  heroSubtitle: string;
  heroCta: string;
  bgPattern: string;
  primaryIconColor: string;
  socialInstagram: string;
  socialTwitter: string;
  socialSnapchat: string;
  socialTiktok: string;
  socialWhatsapp: string;
}

const DEFAULT_SECTION_BG: SectionBgConfig = { bgId: 'inherit', customColor: '' };

export const DEFAULT_DESIGN: DesignSettings = {
  colorPresetId: 'gold',
  fontId: 'cairo',
  radiusId: 'md',
  buttonStyleId: 'filled',
  layoutId: 'default',
  heroStyleId: 'gradient',
  cardStyleId: 'border',
  darkMode: false,
  animationsEnabled: true,
  productGridCols: 4,
  pageBgId: 'black',
  pageBgCustomColor: '#0a0a0a',
  sectionProducts: DEFAULT_SECTION_BG,
  sectionDevices: DEFAULT_SECTION_BG,
  sectionReviews: DEFAULT_SECTION_BG,
  sectionContact: DEFAULT_SECTION_BG,
};

export const DEFAULT_BRAND: BrandSettings = {
  navbarStyle: 'dark',
  navbarHeight: 'md',
  navbarSticky: true,
  navbarShowSearch: true,
  navbarShowWishlist: true,
  navbarShowRewards: true,
  footerStyle: 'dark',
  footerLayout: 'four-col',
  footerShowSocial: true,
  footerShowPayments: true,
  footerShowHours: true,
  footerCopyright: '© 2026 متجر الإبداع — AL Ebdaa Game Store. جميع الحقوق محفوظة.',
  footerTagline: 'متجرك المتخصص في أجهزة الألعاب والإكسسوارات الاحترافية.',
};

export const DEFAULT_IDENTITY: IdentitySettings = {
  logoText: 'إبداع',
  logoSubtext: 'Game Store',
  logoTextStyle: 'gradient',
  logoShape: 'rounded',
  logoIconId: 'game',
  logoImageUrl: '',
  useLogoImage: false,
  heroImageId: 'tech-dark',
  heroImageUrl: '',
  useCustomHeroImage: false,
  heroOverlayOpacity: 40,
  heroTitle: 'أفضل أجهزة الألعاب والإكسسوارات',
  heroSubtitle: 'تسوّق أحدث المنتجات بأفضل الأسعار مع ضمان الجودة والتوصيل السريع',
  heroCta: 'تسوق الآن',
  bgPattern: 'none',
  primaryIconColor: '#f59e0b',
  socialInstagram: '',
  socialTwitter: '',
  socialSnapchat: '',
  socialTiktok: '',
  socialWhatsapp: '',
};

export function loadDesignSettings(): DesignSettings {
  try {
    const raw = localStorage.getItem(DESIGN_SETTINGS_KEY);
    if (raw) return { ...DEFAULT_DESIGN, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_DESIGN;
}

export function loadBrandSettings(): BrandSettings {
  try {
    const raw = localStorage.getItem(BRAND_SETTINGS_KEY);
    if (raw) return { ...DEFAULT_BRAND, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_BRAND;
}

export function loadIdentitySettings(): IdentitySettings {
  try {
    const raw = localStorage.getItem(IDENTITY_SETTINGS_KEY);
    if (raw) return { ...DEFAULT_IDENTITY, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_IDENTITY;
}

export function saveDesignSettings(s: DesignSettings) {
  localStorage.setItem(DESIGN_SETTINGS_KEY, JSON.stringify(s));
}

export function saveBrandSettings(s: BrandSettings) {
  localStorage.setItem(BRAND_SETTINGS_KEY, JSON.stringify(s));
}

export function saveIdentitySettings(s: IdentitySettings) {
  localStorage.setItem(IDENTITY_SETTINGS_KEY, JSON.stringify(s));
}

export type SectionSaveState = 'idle' | 'saving' | 'saved' | 'error';
